/* Native product-gallery integration. Only image media at <=999px are modified. */
(() => {
  if (window.cordilheiraSquareGalleryInstalled) return;
  window.cordilheiraSquareGalleryInstalled = true;
  const mobile = window.matchMedia('(max-width: 999px)');
  const roots = new Map();
  const colors = new Map();
  const neutral = { r: 245, g: 245, b: 245 };
  const rgb = value => `rgb(${value.r}, ${value.g}, ${value.b})`;
  const isWhite = value => value.r >= 204 && value.g >= 204 && value.b >= 204;
  const source = img => img.currentSrc || img.getAttribute('src') || '';

  function sampleCorners(src) {
    if (colors.has(src)) return colors.get(src);
    const promise = new Promise(resolve => {
      const probe = new Image();
      let finished = false;
      const finish = result => {
        if (finished) return;
        finished = true; clearTimeout(timeout); probe.onload = null; probe.onerror = null; resolve(result);
      };
      const timeout = setTimeout(() => finish(null), 10000);
      probe.crossOrigin = 'anonymous';
      probe.decoding = 'async';
      probe.onload = () => {
        try {
          const width = probe.naturalWidth, height = probe.naturalHeight;
          if (!width || !height) return finish(null);
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 1;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          if (!ctx) return finish(null);
          const pixel = (x, y) => {
            ctx.clearRect(0, 0, 1, 1);
            ctx.drawImage(probe, x, y, 1, 1, 0, 0, 1, 1);
            const data = ctx.getImageData(0, 0, 1, 1).data, alpha = data[3] / 255;
            return { r: Math.round(data[0] * alpha + 255 * (1 - alpha)), g: Math.round(data[1] * alpha + 255 * (1 - alpha)), b: Math.round(data[2] * alpha + 255 * (1 - alpha)) };
          };
          const result = { tl: pixel(0, 0), tr: pixel(width - 1, 0), bl: pixel(0, height - 1), br: pixel(width - 1, height - 1) };
          const corners = Object.values(result);
          result.center = Object.fromEntries(['r', 'g', 'b'].map(channel => [channel, Math.round(corners.reduce((sum, value) => sum + value[channel], 0) / 4)]));
          result.imageWidth = width; result.imageHeight = height;
          finish(result);
        } catch { finish(null); }
      };
      probe.onerror = () => finish(null);
      probe.src = src;
    });
    if (colors.size >= 200) colors.delete(colors.keys().next().value);
    colors.set(src, promise);
    return promise;
  }

  class SquareGallery {
    constructor(root) {
      this.root = root;
      this.disposed = false;
      this.events = new AbortController();
      this.processed = new WeakMap();
      this.nearby = new WeakSet();
      this.watched = new WeakSet();
      this.visibility = new IntersectionObserver(entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.nearby.add(entry.target);
          else this.nearby.delete(entry.target);
        }
        this.schedule();
      }, { rootMargin: '200px' });
      this.root.addEventListener('load', () => this.schedule(), { capture: true, signal: this.events.signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(root, { subtree: true, childList: true, attributes: true, attributeFilter: ['src', 'srcset'] });
      this.update();
    }
    schedule() {
      if (!mobile.matches || this.disposed) return;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(), 80);
    }
    update() {
      if (!mobile.matches || this.disposed) return;
      for (const media of this.root.querySelectorAll('.product-gallery__media[data-media-type="image"][data-mobile-square-gallery="true"]')) {
        if (media.closest('product-gallery') !== this.root || media.closest('[class*="thumbnail"]')) continue;
        const img = media.querySelector('img:not([data-mobile-square-helper])');
        if (!img || media.querySelector('video, iframe, model-viewer')) continue;
        if (!this.watched.has(media)) {
          this.watched.add(media);
          this.visibility.observe(media);
        }
        // Never sample offscreen/lazy media: new Image() would bypass lazy loading
        // and download every full-size photo in galleries with many variants.
        if (!this.nearby.has(media) || !img.complete || !img.naturalWidth) continue;
        this.prepare(media, img);
      }
    }
    prepare(media, img) {
      img.setAttribute('data-mobile-square-original', 'true');
      let parent = img.parentElement;
      while (parent && parent !== media) {
        parent.setAttribute(parent.tagName === 'PICTURE' ? 'data-mobile-square-picture' : 'data-mobile-square-wrapper', 'true');
        parent = parent.parentElement;
      }
      const layers = [
        'mobile-square-gallery__corner-gradient',
        'mobile-square-gallery__edge-frost mobile-square-gallery__edge-frost--left',
        'mobile-square-gallery__edge-frost mobile-square-gallery__edge-frost--right',
        'mobile-square-gallery__edge-frost mobile-square-gallery__edge-frost--soft mobile-square-gallery__edge-frost--soft-left',
        'mobile-square-gallery__edge-frost mobile-square-gallery__edge-frost--soft mobile-square-gallery__edge-frost--soft-right'
      ];
      if (!media.querySelector('[data-mobile-square-helper]')) {
        for (const className of layers) {
          const layer = document.createElement('div');
          layer.className = className; layer.setAttribute('data-mobile-square-helper', 'true'); layer.setAttribute('aria-hidden', 'true');
          media.append(layer);
        }
      }
      const chosen = this.root.dataset.squareMode || 'auto';
      if (chosen === 'white' || chosen === 'cover') {
        media.setAttribute('data-mobile-square-mode', chosen);
        return;
      }
      const src = source(img);
      if (!src || this.processed.get(media) === src) return;
      this.processed.set(media, src);
      media.setAttribute('data-mobile-square-mode', 'cover');
      this.apply(media, img, src);
    }
    async apply(media, img, src) {
      const result = await sampleCorners(src);
      if ((this.root.dataset.squareMode || 'auto') !== 'auto') return;
      if (this.disposed || !mobile.matches || !media.isConnected || source(img) !== src) {
        if (source(img) !== src) this.schedule();
        // If a pending sample was interrupted by the desktop breakpoint, retry on mobile.
        if (!mobile.matches) this.processed.delete(media);
        return;
      }
      const blur = !!result && ['tl', 'tr', 'bl', 'br'].every(corner => isWhite(result[corner]));
      for (const key of ['tl', 'tr', 'bl', 'br', 'center']) media.style.setProperty(`--corner-${key}`, rgb(result?.[key] || neutral));
      const width = img.naturalWidth || result?.imageWidth, height = img.naturalHeight || result?.imageHeight;
      const inset = blur && width && height ? Math.max(0, (1 - width / height) / 2) * 100 : 0;
      media.style.setProperty('--mobile-side-inset', `${inset.toFixed(4)}%`);
      media.setAttribute('data-mobile-square-mode', blur ? 'blur' : 'cover');
    }
    destroy() {
      this.disposed = true; clearTimeout(this.timer); this.observer.disconnect(); this.visibility.disconnect(); this.events.abort();
    }
  }
  function scan(scope = document) {
    const galleries = [...scope.querySelectorAll('product-gallery[data-cordilheira-square-gallery]')];
    if (scope.matches?.('product-gallery[data-cordilheira-square-gallery]')) galleries.unshift(scope);
    for (const root of galleries) if (!roots.has(root)) roots.set(root, new SquareGallery(root));
    for (const [root, controller] of roots) if (!root.isConnected) { controller.destroy(); roots.delete(root); }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  else scan();
  document.addEventListener('shopify:section:load', event => scan(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, controller] of roots) if (event.target.contains(root)) { controller.destroy(); roots.delete(root); }
  });
  document.addEventListener('variant:change', () => scan());
  document.addEventListener('product:rerender', () => queueMicrotask(() => scan()));
  mobile.addEventListener('change', () => { if (mobile.matches) { scan(); for (const controller of roots.values()) controller.update(); } });
})();
